/**
 * Μορφοποίηση της Αρχικής: ποσά/ποσοστά σε el-GR (όπως όλη η εφαρμογή), ημερομηνίες και ώρες σε ώρα
 * Ελλάδας (ο server είναι UTC, η επιχείρηση όχι). Οι ημερομηνίες «YYYY-MM-DD» μένουν ημερολογιακές —
 * χωρίς ζώνη ώρας — και μετρούνται στο UTC μεσημέρι, ώστε η αλλαγή ώρας να μη μετακινεί τη μέρα.
 */

import { addDaysYmd, athensYmd } from '../../utils/athensDate';

const nf = (digits: number) => new Intl.NumberFormat('el-GR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const N0 = nf(0);
const N1 = nf(1);
const N2 = nf(2);

const finite = (v: number): number => (Number.isFinite(v) ? v : 0);

export const fmtNum = (v: number): string => N0.format(finite(v));
export const fmtDec1 = (v: number): string => N1.format(finite(v));
export const fmtMoney = (v: number): string => N2.format(finite(v));
export const fmtEur = (v: number): string => `${N2.format(finite(v))} €`;
export const fmtEur0 = (v: number): string => `${N0.format(finite(v))} €`;
export const fmtPct = (v: number): string => `${N1.format(finite(v))}%`;
/** «+70,50 €» / «−12,00 €» (πραγματικό μείον, όχι παύλα). */
export const fmtEurSigned = (v: number): string => `${v < 0 ? '−' : '+'}${fmtEur(Math.abs(v))}`;
/** «+5» / «−3» / «0». */
export const fmtSignedInt = (v: number): string => (v > 0 ? `+${fmtNum(v)}` : v < 0 ? `−${fmtNum(Math.abs(v))}` : '0');

/* ───────── ημερομηνίες ───────── */

export const TZ = 'Europe/Athens';
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const isYmd = (s: unknown): s is string => typeof s === 'string' && YMD_RE.test(s);

// Η ημερομηνία Ελλάδας μιας στιγμής και η ημερολογιακή πρόσθεση ημερών: ένα σημείο για όλη την εφαρμογή.
export { athensYmd, addDaysYmd };

/** «YYYY-MM-DD» → Date στο UTC μεσημέρι (σταθερή μέρα για μορφοποίηση με timeZone UTC). */
export function ymdToDate(ymd: string): Date {
  const m = YMD_RE.exec(ymd);
  if (!m) return new Date(NaN);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
}

/** Ημέρες από `a` ως `b` (b − a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((ymdToDate(b).getTime() - ymdToDate(a).getTime()) / 86400000);
}

/** 0 = Κυριακή … 6 = Σάββατο. */
export const weekdayOf = (ymd: string): number => ymdToDate(ymd).getUTCDay();

/**
 * Ημερομηνία ή χρονική στιγμή του server → ημερομηνία Ελλάδας. «2026-10-13» μένει ως έχει,
 * «2026-10-12T22:30:00Z» γίνεται «2026-10-13» (01:30 ώρα Ελλάδας). Άκυρο → null.
 */
export function toAthensYmd(v: unknown): string | null {
  if (isYmd(v)) return v;
  if (typeof v !== 'string' || !v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : athensYmd(d);
}

export const dateLocale = (lang: string | undefined): string => (lang && lang.startsWith('en') ? 'en-GB' : 'el-GR');

const fmtYmd = (ymd: string, lang: string | undefined, opts: Intl.DateTimeFormatOptions): string => {
  const d = ymdToDate(ymd);
  if (Number.isNaN(d.getTime())) return ymd;
  return new Intl.DateTimeFormat(dateLocale(lang), { timeZone: 'UTC', ...opts }).format(d);
};

/** «Τρίτη 13 Οκτωβρίου». */
export const fmtLongDate = (ymd: string, lang?: string): string => fmtYmd(ymd, lang, { weekday: 'long', day: 'numeric', month: 'long' }).replace(',', '');
/** «13 Οκτωβρίου». */
export const fmtDayMonth = (ymd: string, lang?: string): string => fmtYmd(ymd, lang, { day: 'numeric', month: 'long' });
/** «Τρίτη». */
export const fmtWeekday = (ymd: string, lang?: string): string => fmtYmd(ymd, lang, { weekday: 'long' });
/** «Τρί». */
export const fmtWeekdayShort = (ymd: string, lang?: string): string => fmtYmd(ymd, lang, { weekday: 'short' }).replace('.', '');
/** «13/10». */
export const fmtDM = (ymd: string): string => {
  const m = YMD_RE.exec(ymd);
  return m ? `${Number(m[3])}/${Number(m[2])}` : ymd;
};
/** «13/10/2026» (πεδία ημερομηνίας). */
export const fmtDMY = (ymd: string): string => {
  const m = YMD_RE.exec(ymd);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : ymd;
};

/** «HH:MM» ώρα Ελλάδας μιας στιγμής του server (ή «HH:MM» αυτούσιο). */
export function fmtTime(v: unknown): string {
  if (typeof v === 'string' && /^\d{1,2}:\d{2}$/.test(v)) return v.padStart(5, '0');
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d);
}

/** «06» / «08:00» για ετικέτες ώρας. */
export const hh = (hour: number): string => String(((hour % 24) + 24) % 24).padStart(2, '0');
export const hhmm = (hour: number): string => `${hh(hour)}:00`;

/** «HH:MM» → λεπτά από τα μεσάνυχτα (άκυρο → null). */
export function minutesOf(time: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(time || ''));
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}
